import React, { useState, useRef, useEffect } from "react";
import { Box, CircularProgress, Dialog, DialogContent, DialogActions, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Webcam from "react-webcam";
import BaseCard from "../../../components/Cards/Card";
import CustomButton from "../../../components/CustomButton";
import CustomText, { textStyles } from "../../../components/CustomText";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { BASE_URL } from "../../../components/BaseUrl";
import warning from "../../../assets/warning.svg";

const videoConstraints = {
  width: 400,
  height: 250,
  facingMode: "user",
};

const FaceScanning = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const webcamRef = useRef(null);

  const [capturedImage, setCapturedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [cameraPermission, setCameraPermission] = useState("checking"); // "checking", "granted", "denied"
  const [cameraError, setCameraError] = useState(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);

  // Check camera permission on component mount
  useEffect(() => {
    const checkCameraPermission = async () => {
      try {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true });
          setCameraPermission("granted");
          setCameraError(null);
          // Stop the stream immediately as we just needed to check permission
          stream.getTracks().forEach(track => track.stop());
        } else {
          setCameraPermission("denied");
          setCameraError("Camera not supported on this device");
        }
      } catch (error) {
        setCameraPermission("denied");
        if (error.name === "NotAllowedError") {
          setCameraError("Camera permission denied. Please enable camera access in your browser settings.");
          setShowPermissionModal(true);
        } else if (error.name === "NotFoundError") {
          setCameraError("No camera found on this device.");
        } else {
          setCameraError("Camera access failed. Please check your camera settings.");
        }
      }
    };

    checkCameraPermission();
  }, []);

  const capture = async () => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) return;

    setCapturedImage(imageSrc);
    setUploading(true);

    try {
      const blob = await fetch(imageSrc).then((res) => res.blob());
      const formData = new FormData();
      formData.append("image", blob, "face.jpg");

      const res = await fetch(`${BASE_URL}/upload/image`, {
        method: "POST",
        body: formData,
      });

      const rawText = await res.text();
      let data;
      try {
        data = JSON.parse(rawText);
      } catch {
        toast.error("Invalid server response");
        return;
      }

      if (!res.ok || data.error) {
        toast.error(data.message || "Image upload failed");
        return;
      }

      if (data.data?.url) {
        toast.success("Face captured & saved!");
        // ✅ Pass image url to next screen
        navigate("/registration-verification", { state: { faceUrl: data.data.url } });
      }
    } catch (err) {
      console.error("❌ Upload error:", err);
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ py: 6, px: 2, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
      <BaseCard maxWidth="40%">
        <CustomText sx={{ ...textStyles.h4, fontSize: "26px", mb: 2 }}>
          {t("faceScanTitle")}
        </CustomText>
        <CustomText sx={{ ...textStyles.body5, mb: 3 }}>
          {t("faceScanSubtitle")}
        </CustomText>

        {/* Camera Permission States */}
        {cameraPermission === "checking" && (
          <Box sx={{ 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center", 
            gap: 2, 
            py: 4,
            textAlign: "center"
          }}>
            <CircularProgress size={40} />
            <CustomText sx={{ ...textStyles.body5, color: "#666" }}>
              Checking camera permission...
            </CustomText>
          </Box>
        )}

        {cameraPermission === "denied" && !showPermissionModal && cameraError && !cameraError.includes("permission denied") && (
          <Box sx={{ 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center", 
            gap: 2, 
            py: 4,
            textAlign: "center",
            backgroundColor: "#ffebee",
            borderRadius: 2,
            border: "1px solid #f44336"
          }}>
            <Box sx={{ 
              width: 60, 
              height: 60, 
              borderRadius: "50%", 
              backgroundColor: "#f44336", 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              color: "white",
              fontSize: "24px"
            }}>
              📷
            </Box>
            <CustomText sx={{ ...textStyles.h6, color: "#d32f2f", fontWeight: 600 }}>
              Camera Error
            </CustomText>
            <CustomText sx={{ ...textStyles.body5, color: "#666", maxWidth: "80%" }}>
              {cameraError}
            </CustomText>
            <CustomButton
              label="Retry Camera Access"
              onClick={() => window.location.reload()}
              sx={{ mt: 2 }}
            />
          </Box>
        )}

        {cameraPermission === "granted" && !capturedImage && (
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            videoConstraints={videoConstraints}
            style={{ width: "100%", borderRadius: 8 }}
            onUserMediaError={(error) => {
              setCameraPermission("denied");
              setCameraError("Camera access failed. Please check your camera settings.");
            }}
          />
        )}

        {cameraPermission === "granted" && capturedImage && (
          <img src={capturedImage} alt="Captured" style={{ width: "100%", borderRadius: 8 }} />
        )}

        {cameraPermission === "granted" && (
          <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3 }}>
            {!capturedImage ? (
              <CustomButton
                label={uploading ? "Uploading..." : t("faceScanCapture")}
                onClick={capture}
                disabled={uploading}
              />
            ) : (
              <CustomButton
                label={t("retake")}
                onClick={() => setCapturedImage(null)}
              />
            )}
          </Box>
        )}
      </BaseCard>

      {/* Camera Permission Modal */}
      <Dialog
        open={showPermissionModal}
        fullWidth
        maxWidth="sm"
        onClose={() => setShowPermissionModal(false)}
        PaperProps={{
          sx: {
            px: 1,
            py: 2,
            borderRadius: "12px",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 1 }}>
          <img src={warning} style={{ width: 50, height: 50 }} />
        </Box>
        
        {/* Title */}
        <CustomText
          sx={{
            ...textStyles.h1,
            fontSize: 18,
            fontWeight: 600,
            color: "#2C384C",
            mb: 2,
            pt: 0.5,
          }}
        >
          Turn on camera to continue
        </CustomText>

        <DialogContent dividers>
          {/* Browser Instructions */}
          <Box sx={{ mb: 3 }}>
            <CustomText sx={{ fontSize: 12, color: "#172B4D", mb: 1, fontFamily: 'Poppins, sans-serif' }}>
              <strong>Chrome/Edge:</strong> Click the padlock icon in the address bar → 
              Site settings → Camera → Allow, then reload.
            </CustomText>
            <CustomText sx={{ fontSize: 12, color: "#172B4D", mb: 1, fontFamily: 'Poppins, sans-serif' }}>
              <strong>Firefox:</strong> Click the shield/info icon → Permissions → 
              Camera → Allow, then reload.
            </CustomText>
            <CustomText sx={{ fontSize: 12, color: "#172B4D", fontFamily: 'Poppins, sans-serif' }}>
              <strong>Safari (macOS):</strong> Safari Settings → Websites → Camera → 
              Allow, then reload.
            </CustomText>
          </Box>
        </DialogContent>

        <Box sx={{ mt: 2, mb: 0.5, display: 'flex', flexWrap: "wrap", justifyContent: "flex-end", gap: 2 }}>
          <CustomButton
            onClick={() => window.location.reload()}
            label="I've enabled it"
            size="medium"
          />
          
          <Button
            variant="outlined"
            onClick={() => setShowPermissionModal(false)}
            sx={{
              fontSize: 15,
              textTransform: 'none',
              borderRadius: '5px',
              fontFamily: 'Poppins, sans-serif',
              color: '#006EC2',
              border: "2px solid #006EC2",
              px: 3,
              py: 1,
              height: 'fit-content',
            }}
          >
            Cancel
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default FaceScanning;


